import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, Search } from "lucide-react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import {
  fmtMoney,
  fmtPct,
  fmtRatio,
  verdictStyle,
  marginColor,
  MARKETS,
  normalizeTicker,
  marketFromTicker,
  displayTicker,
} from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

export default function Compare() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initial = (searchParams.get("tickers") || "")
    .split(",")
    .map((t) => t.trim().toUpperCase())
    .filter(Boolean);
  const [tickers, setTickers] = useState(initial);
  const [newTicker, setNewTicker] = useState("");
  const [market, setMarket] = useState(
    initial.length > 0 ? marketFromTicker(initial[0]) : "US"
  );
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (tList) => {
    if (tList.length === 0) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/stocks/compare/batch?tickers=${tList.join(",")}`);
      setResults(data.results || []);
    } catch {
      toast.error("Could not load comparison");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(tickers);
    setSearchParams({ tickers: tickers.join(",") });
    // eslint-disable-next-line
  }, [tickers]);

  const addTicker = (e) => {
    e?.preventDefault();
    const t = normalizeTicker(newTicker, market);
    if (!t || tickers.includes(t) || tickers.length >= 8) return;
    setTickers([...tickers, t]);
    setNewTicker("");
  };

  const removeTicker = (t) => {
    setTickers(tickers.filter((x) => x !== t));
  };

  return (
    <div className="min-h-screen bg-bone-50" data-testid="compare-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="mb-10">
          <div className="overline mb-3">— Multi-Stock Evaluation</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-stone_brand-dark tracking-tight font-semibold">
            Compare stocks.
          </h1>
          <p className="text-stone_brand mt-2">
            Ranked by <span className="font-mono">margin of safety</span> — most undervalued first.
          </p>
        </div>

        <div className="bg-white border border-line rounded-sm p-5 mb-8">
          <form onSubmit={addTicker} className="flex flex-col gap-3 mb-4">
            <div className="flex items-center gap-1.5" data-testid="compare-market-selector">
              {MARKETS.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMarket(m.key)}
                  className={`px-3 py-1 rounded-sm text-xs font-mono tracking-wider transition-colors ${
                    market === m.key
                      ? "bg-moss text-bone-50"
                      : "bg-white border border-line text-stone_brand hover:border-moss/50"
                  }`}
                  data-testid={`compare-market-${m.key}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone_brand-light" />
                <Input
                  value={newTicker}
                  onChange={(e) => setNewTicker(e.target.value.toUpperCase())}
                  placeholder={MARKETS.find((m) => m.key === market)?.hint}
                  className="pl-9 rounded-sm border-line bg-white font-mono h-10 focus-visible:ring-moss"
                  data-testid="compare-add-input"
                />
              </div>
              <Button
                type="submit"
                disabled={!newTicker.trim() || tickers.length >= 8}
                className="rounded-sm bg-moss hover:bg-moss-hover text-bone-50 h-10"
                data-testid="compare-add-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </form>
          <div className="flex flex-wrap gap-2">
            {tickers.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-2 bg-bone-100 border border-line px-3 py-1 rounded-sm font-mono text-sm text-stone_brand-dark"
                data-testid={`compare-chip-${t}`}
              >
                {displayTicker(t)}
                <span className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light border border-line px-1 rounded-sm">
                  {marketFromTicker(t)}
                </span>
                <button
                  onClick={() => removeTicker(t)}
                  className="text-stone_brand-light hover:text-terracotta transition-colors"
                  aria-label={`Remove ${t}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {tickers.length === 0 && (
              <span className="text-sm text-stone_brand italic">
                Add up to 8 tickers to compare.
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="grid gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 bg-white border border-line rounded-sm animate-pulse" />
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="bg-white border border-line rounded-sm p-12 text-center">
            <div className="font-serif-display text-xl text-stone_brand-dark mb-2">
              Nothing to compare yet
            </div>
            <p className="text-sm text-stone_brand">Add a ticker above to begin.</p>
          </div>
        ) : (
          <div className="bg-white border border-line rounded-sm overflow-x-auto" data-testid="compare-table">
            <table className="w-full">
              <thead>
                <tr className="border-b border-line text-left">
                  {["#", "Ticker", "Price", "Mean Target", "Margin of Safety", "P/E", "Forward P/E", "ROE", "Verdict"].map((h) => (
                    <th
                      key={h}
                      className="overline py-4 px-4 text-stone_brand-light whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((s, i) => {
                  const cur = s.currency || "USD";
                  const mkt = marketFromTicker(s.ticker);
                  return (
                    <tr
                      key={s.ticker}
                      onClick={() => navigate(`/stock/${encodeURIComponent(s.ticker)}`)}
                      className="border-b border-line last:border-0 hover:bg-bone-100 cursor-pointer transition-colors"
                      data-testid={`compare-row-${s.ticker}`}
                    >
                      <td className="py-4 px-4 font-mono text-sm text-stone_brand-light">
                        {String(i + 1).padStart(2, "0")}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold text-stone_brand-dark">
                            {displayTicker(s.ticker)}
                          </span>
                          <span className="text-[0.6rem] font-mono uppercase tracking-widest text-stone_brand-light border border-line px-1 rounded-sm">
                            {mkt}
                          </span>
                        </div>
                        <div className="text-xs text-stone_brand truncate max-w-[200px]">
                          {s.company_name}
                        </div>
                      </td>
                      <td className="py-4 px-4 font-mono text-stone_brand-dark">
                        {fmtMoney(s.current_price, cur)}
                      </td>
                      <td className="py-4 px-4 font-mono text-stone_brand-dark">
                        {fmtMoney(s.analyst_target_mean, cur)}
                      </td>
                      <td className={`py-4 px-4 font-mono font-semibold ${marginColor(s.margin_of_safety_percent)}`}>
                        {fmtPct(s.margin_of_safety_percent)}
                      </td>
                      <td className="py-4 px-4 font-mono text-stone_brand-dark">
                        {fmtRatio(s.pe_ratio)}
                      </td>
                      <td className="py-4 px-4 font-mono text-stone_brand-dark">
                        {fmtRatio(s.forward_pe)}
                      </td>
                      <td className="py-4 px-4 font-mono text-stone_brand-dark">
                        {s.return_on_equity !== null ? fmtPct(s.return_on_equity * 100) : "—"}
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`px-2.5 py-0.5 rounded-sm text-xs font-mono font-medium border ${verdictStyle(s.verdict)}`}
                        >
                          {s.verdict}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
